/**
 * Live provisioning-progress checklist (OCP-205).
 *
 * Polls `GET /v1/portal/instances/:id/lifecycle` every 2s while the
 * tenant is still provisioning, renders a 9-step canonical checklist
 * with per-step status icons (pending/in-progress/completed/failed).
 *
 * Polling stops automatically when:
 *   - response.is_terminal === true (tenant reached active OR any step failed)
 *   - component unmounts
 *   - document becomes hidden (browser tab switched away)
 *
 * Polling is the Stage-1 choice; SSE upgrade-path: swap the
 * setInterval with EventSource('/v1/portal/instances/:id/lifecycle/stream')
 * once concurrent provisions warrant the extra plumbing. The service
 * data-source (tenant_lifecycle_event) stays the same.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api } from '../lib/api';

interface LifecycleStep {
  name: string;
  index: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
}

interface LifecycleResponse {
  tenant_status: string;
  steps: LifecycleStep[];
  is_terminal: boolean;
  /**
   * OCP-488 - true ONLY on a permanent provision failure (retries
   * exhausted). A transient failed step is not permanent (pg-boss may
   * still recover it), so the customer is never shown a hard error - the
   * UI keeps a calm "still setting up" tone and self-heals to ready.
   */
  provisioning_failed: boolean;
}

/** The canonical pipeline steps with a label (`instance-detail:provisioning.step.<name>`, OCP-656). */
const STEP_NAMES = new Set([
  'pick_worker',
  'provision_storage',
  'coolify_create_service',
  'coolify_set_envs',
  'coolify_set_domain',
  'coolify_start',
  'cloudflare_cname',
  'poll_ready',
  'welcome_email',
]);

interface Props {
  instanceId: string;
  /**
   * Called when polling enters a terminal state. `failed` is true only on a
   * PERMANENT provision failure (OCP-488); a transient failed step keeps
   * polling and does not fire this.
   */
  onTerminal?: (result: { status: string; failed: boolean }) => void;
  /** Polling interval; default 2000ms. */
  pollIntervalMs?: number;
}

export function ProvisioningProgress({
  instanceId,
  onTerminal,
  pollIntervalMs = 2000,
}: Props) {
  const { t } = useTranslation('instance-detail');
  const [data, setData] = useState<LifecycleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let terminalAnnounced = false;

    async function tick() {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) {
        // Tab not visible: skip the fetch + retry later.
        timer = setTimeout(tick, pollIntervalMs);
        return;
      }
      try {
        const r = await api<LifecycleResponse>(
          `/v1/portal/instances/${instanceId}/lifecycle`,
        );
        if (cancelled) return;
        setData(r);
        if (r.is_terminal) {
          if (!terminalAnnounced) {
            terminalAnnounced = true;
            onTerminal?.({ status: r.tenant_status, failed: r.provisioning_failed });
          }
          return; // stop polling
        }
        timer = setTimeout(tick, pollIntervalMs);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError(t('provisioning.gone'));
          return;
        }
        // Transient error: keep retrying so we recover from a blip.
        timer = setTimeout(tick, pollIntervalMs);
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [instanceId, onTerminal, pollIntervalMs, t]);

  if (error) {
    return (
      <div
        role="alert"
        data-testid="provisioning-error"
        className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
      >
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-gray-500" data-testid="provisioning-loading">
        {t('provisioning.loading')}
      </p>
    );
  }

  // OCP-488 - the customer is NEVER shown a hard error. A transient failed
  // step (pg-boss still retrying) is presented as "still working"; only a
  // permanent failure flips the tone, and even then it stays calm (the
  // wrapper shows the "we'll email you" message + the operator is paged).
  const ready = data.is_terminal && data.tenant_status === 'active';

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {ready
            ? t('provisioning.ready')
            : data.provisioning_failed
              ? t('provisioning.almostThere')
              : t('provisioning.spinningUp')}
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          {ready
            ? t('provisioning.readyBody')
            : data.provisioning_failed
              ? t('provisioning.failedBody')
              : t('provisioning.runningBody')}
        </p>
      </div>
      <ol
        className="space-y-2"
        data-testid="provisioning-steps"
      >
        {data.steps.map((step) => (
          <StepRow key={step.name} step={step} />
        ))}
      </ol>
    </div>
  );
}

function StepRow({ step }: { step: LifecycleStep }) {
  const { t } = useTranslation('instance-detail');
  const label = STEP_NAMES.has(step.name) ? t(`provisioning.step.${step.name}`) : step.name;
  // OCP-488 - a customer never sees a red failed step or the raw internal
  // error string. A `failed` step is being retried by pg-boss, so render it
  // exactly like an in-progress step ("still working"). The permanent-failure
  // case is handled at the panel level (calm "we'll email you" message).
  const displayStatus: LifecycleStep['status'] =
    step.status === 'failed' ? 'in_progress' : step.status;
  return (
    <li
      data-testid={`provisioning-step-${step.name}`}
      data-status={displayStatus}
      className="flex items-start gap-3"
    >
      <StatusIcon status={displayStatus} />
      <div className="flex-1">
        <p
          className={`text-sm ${
            displayStatus === 'completed'
              ? 'text-gray-700 dark:text-gray-300'
              : displayStatus === 'in_progress'
                ? 'font-medium text-gray-900 dark:text-gray-100'
                : 'text-gray-400'
          }`}
        >
          {label}
        </p>
      </div>
    </li>
  );
}

function StatusIcon({ status }: { status: LifecycleStep['status'] }) {
  const { t } = useTranslation('instance-detail');
  if (status === 'completed') {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span
        aria-label={t('provisioning.inProgress')}
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center"
      >
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  // pending
  return (
    <span
      aria-label={t('provisioning.pending')}
      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 dark:border-gray-700"
    />
  );
}
